import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Music, SkipForward, SkipBack } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';

export interface SongItem {
  id: string;
  url: string;
  title: string;
  artist: string;
  coverArt: string;
  duration: number; // in seconds
}

interface PostMusicPlayerProps {
  song: SongItem;
  className?: string;
  autoPlay?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  hasMultipleSongs?: boolean;
}

/**
 * Music player component specifically designed for PostCard
 * Provides a beautiful, modern interface for playing songs
 * Features vinyl record animation and waveform visualization
 * Supports autoplay on scroll functionality and multi-song navigation
 */
const PostMusicPlayer: React.FC<PostMusicPlayerProps> = ({ 
  song, 
  className = '', 
  autoPlay = true,
  onNext,
  onPrevious,
  hasMultipleSongs = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isVolumeControlVisible, setIsVolumeControlVisible] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isVinylRotating, setIsVinylRotating] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Use intersection observer to detect when music player is in viewport
  const { ref: viewRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });
  
  // Connect to the global music player context
  const { setCurrentTrack } = useMusicPlayer();

  // Format time from seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Generate random waveform data for visualization
  useEffect(() => {
    // Generate random waveform data (would normally come from audio analysis)
    const generateWaveform = () => {
      const data: number[] = [];
      for (let i = 0; i < 40; i++) {
        // Create a more musical pattern with some randomness
        const baseHeight = Math.sin(i * 0.2) * 0.5 + 0.5; // Sine wave pattern
        const randomFactor = Math.random() * 0.3; // Random variation
        data.push(baseHeight + randomFactor);
      }
      setWaveformData(data);
    };

    generateWaveform();
  }, [song.id]);

  // Draw waveform visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const drawWaveform = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = canvas.width / waveformData.length;
      const barGap = 2;
      const playedRatio = currentTime / song.duration;
      
      waveformData.forEach((value, index) => {
        const barHeight = value * canvas.height * 0.8;
        const x = index * (barWidth + barGap);
        const y = (canvas.height - barHeight) / 2;
        
        // Determine if this bar has been "played"
        const isPlayed = index / waveformData.length < playedRatio;
        
        // Set color based on played status
        ctx.fillStyle = isPlayed ? '#065900' : '#d1d5db';
        if (isPlaying && isPlayed) {
          // Add pulsing effect to played bars when playing
          const pulse = Math.sin(Date.now() / 200 + index) * 0.1 + 0.9;
          ctx.fillStyle = '#065900';
          ctx.globalAlpha = pulse;
        } else {
          ctx.globalAlpha = 1.0;
        }
        
        // Draw rounded bars
        const radius = barWidth / 2;
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, radius);
        ctx.fill();
      });
    };

    // Animation frame for waveform
    let animationId: number;
    const animate = () => {
      drawWaveform();
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [waveformData, currentTime, isPlaying, song.duration]);

  // Handle autoplay on scroll visibility changes
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    
    if (inView && autoPlay && !isPlaying) {
      // Pause any other audio that might be playing
      document.querySelectorAll('audio').forEach(audio => {
        const otherAudioElement = audio as HTMLAudioElement;
        if (otherAudioElement !== audioRef.current && !otherAudioElement.paused) {
          otherAudioElement.pause();
        }
      });
      
      // Attempt to play with error handling
      const attemptPlay = () => {
        audioElement.play().then(() => {
          setIsPlaying(true);
          setIsVinylRotating(true);
        }).catch(error => {
          // Silently handle autoplay failure - this is expected browser behavior
          // User can manually play if they want to hear audio
          if (process.env.NODE_ENV === 'development') {
            console.warn("Audio autoplay blocked by browser policy");
          }
        });
      };
      
      attemptPlay();
    } else if (!inView && isPlaying) {
      // Pause when out of view
      audioElement.pause();
      setIsPlaying(false);
      setIsVinylRotating(false);
    }
  }, [inView, autoPlay, isPlaying]);

  useEffect(() => {
    // Set up audio element
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
      
      // Set up event listeners
      const audio = audioRef.current;
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setIsVinylRotating(false);
        audio.currentTime = 0;
        
        // Auto advance to next song if available
        if (hasMultipleSongs && onNext) {
          setTimeout(() => onNext(), 500);
        }
      };
      
      const handlePlay = () => {
        setIsPlaying(true);
        setIsVinylRotating(true);
      };
      
      const handlePause = () => {
        setIsPlaying(false);
        setIsVinylRotating(false);
      };
      
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
      };
    }
  }, [volume, isMuted, hasMultipleSongs, onNext]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Pause any other audio that might be playing
        document.querySelectorAll('audio').forEach(audio => {
          const audioElement = audio as HTMLAudioElement;
          if (audioElement !== audioRef.current && !audioElement.paused) {
            audioElement.pause();
          }
        });
        
        audioRef.current.play().catch(error => {
          console.error("Error playing audio:", error);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const newTime = clickPosition * song.duration;
      
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.volume = !isMuted ? 0 : volume;
    }
  };

  const openFullPlayer = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    if (setCurrentTrack) {
      setCurrentTrack({
        id: song.id,
        title: song.title,
        artist: song.artist,
        artwork: song.coverArt,
        url: song.url
      });
    }
  };

  // Handle navigation between songs
  const handlePreviousSong = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onPrevious) {
      onPrevious();
    }
  };

  const handleNextSong = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onNext) {
      onNext();
    }
  };

  return (
    <div ref={viewRef} className={`bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 rounded-lg overflow-hidden shadow-lg w-full h-full ${className}`}>
      <audio ref={audioRef} src={song.url} preload="metadata" />
      
      <div className="pt-4 px-4 pb-2 h-full flex flex-col">
        {/* Top Section with Vinyl Record and Cover Art */}
        <div className="flex justify-center mb-4 relative">
          {/* Vinyl Record Animation */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[180px] h-[180px] rounded-full bg-black z-0">
            {/* Vinyl grooves */}
            <div className="absolute inset-0 rounded-full border-4 border-gray-800 opacity-30"></div>
            <div className="absolute inset-[15px] rounded-full border-2 border-gray-800 opacity-30"></div>
            <div className="absolute inset-[30px] rounded-full border-2 border-gray-800 opacity-30"></div>
            <div className="absolute inset-[45px] rounded-full border-2 border-gray-800 opacity-30"></div>
            <div className="absolute inset-[60px] rounded-full border-2 border-gray-800 opacity-30"></div>
            <div className="absolute inset-[75px] rounded-full border-2 border-gray-800 opacity-30"></div>
            {/* Center hole */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[12px] h-[12px] rounded-full bg-white dark:bg-gray-300"></div>
          </div>
          
          {/* Album Cover */}
          <div 
            className={`w-[190px] h-[190px] rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden z-10 shadow-xl ${isVinylRotating ? 'animate-spin-slow' : ''}`}
            onClick={openFullPlayer}
            style={{ 
              animationDuration: '3s',
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite'
            }}
          >
            <img 
              src={song.coverArt} 
              alt={song.title} 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Song Info */}
        <div className="text-center mb-4">
          <h3 className="font-bold text-lg truncate dark:text-white">{song.title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{song.artist}</p>
        </div>
        
        {/* Waveform Visualization */}
        <div className="mb-4 h-16 relative">
          <canvas 
            ref={canvasRef} 
            className="w-full h-full cursor-pointer" 
            width={400} 
            height={64}
            onClick={(e) => {
              const canvas = canvasRef.current;
              if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const ratio = x / rect.width;
                if (audioRef.current) {
                  audioRef.current.currentTime = ratio * song.duration;
                }
              }
            }}
          />
        </div>
        
        {/* Progress Bar */}
        <div 
          ref={progressBarRef}
          className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer mb-2 overflow-hidden"
          onClick={handleProgressClick}
        >
          <div 
            className="h-full bg-[#065900] rounded-full transition-all duration-100 ease-linear" 
            style={{ width: `${(currentTime / song.duration) * 100}%` }}
          />
        </div>
        
        {/* Time Display */}
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(song.duration)}</span>
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mb-1">
          {/* Previous Song Button (only if multiple songs) */}
          {hasMultipleSongs && (
            <button 
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-[#065900] dark:hover:text-[#065900] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={handlePreviousSong}
              aria-label="Previous song"
            >
              <SkipBack className="w-5 h-5" />
            </button>
          )}
          
          {/* Play/Pause Button */}
          <button 
            className="w-12 h-12 rounded-full bg-[#065900] flex items-center justify-center text-white hover:bg-[#054800] transition-colors shadow-md"
            onClick={togglePlayPause}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </button>
          
          {/* Next Song Button (only if multiple songs) */}
          {hasMultipleSongs && (
            <button 
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-[#065900] dark:hover:text-[#065900] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={handleNextSong}
              aria-label="Next song"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          )}
          
          {/* Volume Control */}
          <div className="relative ml-2">
            <button 
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-[#065900] dark:hover:text-[#065900] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={toggleMute}
              onMouseEnter={() => setIsVolumeControlVisible(true)}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
            
            {isVolumeControlVisible && (
              <div 
                className="absolute bottom-full right-0 mb-2 p-2 bg-white dark:bg-gray-800 rounded shadow-lg z-10"
                onMouseEnter={() => setIsVolumeControlVisible(true)}
                onMouseLeave={() => setIsVolumeControlVisible(false)}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-24 accent-[#065900]"
                />
              </div>
            )}
          </div>
          
          {/* Music Icon */}
          <button 
            className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-[#065900] dark:hover:text-[#065900] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={openFullPlayer}
            aria-label="Open full player"
          >
            <Music className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostMusicPlayer;
